import { useToast } from "@design";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plug } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  authorize,
  clearMyConnectorCredentialMutation,
  disconnectMutation,
  errorMessage,
  listAvailableOptions,
  listAvailableQueryKey,
  listExtensionConsentOptions,
  listExtensionConsentQueryKey,
  listMyConnectorConnectionsOptions,
  listMyConnectorConnectionsQueryKey,
  type MyConnectorConnection,
} from "@/api";
import { EmptyState } from "@/components/build/EmptyState";
import { Page } from "@/components/Page";
import { QuerySection } from "@/components/section/QuerySection";
import { PageSection } from "@/components/section/SectionBlocks";
import { ConnectorConnections } from "./ConnectorConnections";
import { type AccountActions, type Service, ServicesList } from "./ServicesList";
import { useConnectionCallback } from "./useConnectionCallback";

const CALLBACK_PATH = "/settings/connections";

/** Services I can turn on: each integration once, with its personal sign-in or account if it has one. */
function useServices() {
  const consent = useQuery(listExtensionConsentOptions());
  const available = useQuery(listAvailableOptions());
  const mine = useQuery(listMyConnectorConnectionsOptions());
  const services = useMemo(() => {
    const personal = new Map(
      (mine.data?.data ?? []).filter((c) => c.integrationSlug).map((c) => [c.integrationSlug as string, c]),
    );
    const accounts = new Map((available.data ?? []).map((a) => [a.slug, a]));
    const out: Service[] = (consent.data?.data ?? [])
      .filter((ext) => ext.read.present || ext.write.present || personal.has(ext.slug) || accounts.has(ext.slug))
      .map((ext) => ({
        slug: ext.slug,
        name: ext.name,
        description: ext.description,
        consent: ext,
        personal: personal.get(ext.slug),
        account: accounts.get(ext.slug),
      }));
    const listed = new Set(out.map((s) => s.slug));
    for (const account of available.data ?? []) {
      if (!listed.has(account.slug)) {
        listed.add(account.slug);
        out.push({ slug: account.slug, name: account.name, description: account.description, account });
      }
    }
    for (const [slug, connection] of personal) {
      if (!listed.has(slug))
        out.push({ slug, name: connection.name, description: connection.description, personal: connection });
    }
    return out.sort((a, b) => a.name.localeCompare(b.name));
  }, [consent.data, available.data, mine.data]);
  const nameFor = useCallback((slug: string) => services.find((s) => s.slug === slug)?.name, [services]);
  const queries = [consent, available, mine];
  // One state for the three lists, so a failure shows an error (with retry), never "nothing here".
  const query = {
    data: queries.every((q) => q.isSuccess) ? services : undefined,
    isPending: queries.some((q) => q.isPending),
    isError: queries.some((q) => q.isError),
    refetch: () => Promise.all(queries.filter((q) => q.isError).map((q) => q.refetch())),
  };
  return { services, query, loading: query.isPending, nameFor };
}

function useAccountActions(nameFor: (slug: string) => string | undefined): AccountActions {
  const { t } = useTranslation("settings");
  const toast = useToast();
  const queryClient = useQueryClient();
  const [busySlug, setBusySlug] = useState<string | null>(null);
  const refresh = () =>
    Promise.all(
      [listAvailableQueryKey(), listExtensionConsentQueryKey(), listMyConnectorConnectionsQueryKey()].map((queryKey) =>
        queryClient.invalidateQueries({ queryKey }),
      ),
    );
  const disconnect = useMutation({
    ...disconnectMutation(),
    onSuccess: async (_data, vars) => {
      await refresh();
      toast.success(t("connections.disconnected", { name: nameFor(vars.path.slug) ?? vars.path.slug }));
    },
    onError: (error) => toast.error(errorMessage(error, t("connections.disconnectFailed"))),
    onSettled: () => setBusySlug(null),
  });
  const signOut = useMutation(clearMyConnectorCredentialMutation());

  return {
    busySlug,
    connect: async (slug: string) => {
      setBusySlug(slug);
      try {
        const { data } = await authorize({
          path: { slug },
          query: { redirect_after: `${window.location.origin}${CALLBACK_PATH}` },
          throwOnError: true,
        });
        window.location.assign(data.authorizeUrl);
      } catch (error) {
        setBusySlug(null);
        toast.error(errorMessage(error, t("connections.connectFailed")));
      }
    },
    disconnect: (slug: string) => {
      setBusySlug(slug);
      disconnect.mutate({ path: { slug } });
    },
    signOut: (connection: MyConnectorConnection, slug: string) => {
      setBusySlug(`signout:${slug}`);
      signOut.mutate(
        { path: { connector_uuid: connection.uuid } },
        {
          onSuccess: async () => {
            await refresh();
            toast.success(t("connections.services.signedOut", { name: nameFor(slug) ?? connection.name }));
          },
          onError: (error) => toast.error(errorMessage(error, t("connections.disconnectFailed"))),
          onSettled: () => setBusySlug(null),
        },
      );
    },
  };
}

/**
 * Settings > Connections (phase 7): one place to turn services on and off. Services that act as me
 * (my own Slack) have Sign in; accounts (Asana) have Connect; the rest a switch. Connectors shared
 * with me that need my own token follow.
 */
export function ConnectionsPage() {
  const { t } = useTranslation("settings");
  const { query, loading, nameFor } = useServices();
  const accounts = useAccountActions(nameFor);
  useConnectionCallback(nameFor, !loading);
  const others = useQuery({
    ...listMyConnectorConnectionsOptions(),
    select: (r) => r.data.filter((c) => !c.integrationSlug),
  });

  return (
    <Page title={t("connections.title")} subtitle={t("connections.subtitle")}>
      <QuerySection
        query={query}
        isEmpty={(services) => services.length === 0 && (others.data?.length ?? 0) === 0}
        empty={{
          view: (
            <EmptyState icon={<Plug size={32} />} title={t("connections.empty")} body={t("connections.emptyBody")} />
          ),
        }}
      >
        {(services) =>
          services.length > 0 && (
            <PageSection title={t("connections.services.title")} description={t("connections.services.body")}>
              <ServicesList services={services} accounts={accounts} />
            </PageSection>
          )
        }
      </QuerySection>
      <ConnectorConnections />
    </Page>
  );
}
